<template>
  <div
    v-if="isVisible"
    class="plan-guard-bindings">
    <a-select
      v-model:value="selectedIds"
      class="plan-guard-bindings__select"
      mode="multiple"
      option-filter-prop="label"
      :options="options"
      :loading="isLoading"
      :disabled="!canEdit"
      :placeholder="placeholder" />

    <a-button
      v-if="canEdit"
      type="primary"
      :disabled="!isDirty"
      :loading="isSaving"
      @click="onSave">
      Сохранить
    </a-button>
  </div>
</template>

<script setup>
import {
  computed, onMounted, ref, watch,
} from 'vue';
import { useStore } from 'vuex';
import { message } from 'ant-design-vue';
import getFlag from '@/helpers/getFlag';
import { GuardOwnerField, GuardOwnerType } from '@/enums/guard';

const props = defineProps({
  ownerType: {
    type: String,
    required: true,
    validator: (value) => Object.values(GuardOwnerType).includes(value),
  },
  ownerId: {
    type: [Number, String],
    required: true,
  },
  placeholder: {
    type: String,
    default: 'Проверки не привязаны',
  },
});

const store = useStore();

const isVisible = getFlag('isGuardsVisible');
const canEdit = store.getters['auth/hasPermission']('api.plan_guards.bindings.sync_crm_plan');
const isSaving = ref(false);
const selectedIds = ref([]);

const guards = computed(() => store.state.guards.items);
const isLoading = computed(() => store.state.guards.isLoading);

const options = computed(() => guards.value
  .map((guard) => ({ value: guard.id, label: guard.title?.ru || guard.name })));

const boundIds = computed(() => guards.value
  .filter((guard) => (guard[GuardOwnerField[props.ownerType]] ?? [])
    .some((owner) => String(owner.id) === String(props.ownerId)))
  .map((guard) => guard.id));

const isDirty = computed(() => {
  const bound = [...boundIds.value].sort();
  const selected = [...selectedIds.value].sort();
  return bound.length !== selected.length || bound.some((id, index) => id !== selected[index]);
});

const onSave = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('guards/syncBindings', {
      ownerType: props.ownerType,
      ownerId: props.ownerId,
      guardIds: selectedIds.value,
    });
    message.success('Привязки проверок сохранены');
  } catch (error) {
    message.error('Не удалось сохранить привязки проверок');
    throw error;
  } finally {
    isSaving.value = false;
  }
};

watch(boundIds, (value) => {
  selectedIds.value = [...value];
}, { immediate: true });

onMounted(() => {
  if (!isVisible) return;
  if (guards.value.length === 0 && !isLoading.value) store.dispatch('guards/getList');
});
</script>

<style scoped>
.plan-guard-bindings {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}

.plan-guard-bindings__select {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
